import { useEffect, useState } from "react";
import classNames from "classnames";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "store";
import UserRole from "enums/UserRole";
import {
  Dropdown,
  Navbar,
  Loader,
  Avatar,
  HomeIcon,
  UserCircleIcon,
  BuildingHospitalIcon,
  SettingsIcon,
  PaletteIcon,
  Select,
  SelectOption,
  BuildingIcon,
  CookiePopup,
  Status,
  AlertTriangleIcon,
} from "@mwi/ui";
import { login, logout, clearAuth } from "slices/auth";
import {
  useEndSwitchPracticeMutation,
  useGetHeartbeatQuery,
  useGetMeQuery,
  useGetPracticesQuery,
  useLogoutMutation,
  useSwitchPracticeMutation,
} from "api";
import { clearPractice, setPractice } from "slices/practice";
import { useForm } from "react-hook-form";
import SwitchPracticeModal from "components/modals/admin/switch_practice";

type Inputs = {
  practice: string | undefined;
};

const App = ({ children }: { children: JSX.Element }) => {
  const dispatch = useDispatch();

  const [isSwitching, setIsSwitching] = useState(false);
  const [switchPracticeRedirect, setSwitchPracticeRedirect] =
    useState<string>();

  const { user, theme, practiceId, tempColours, isAuthenticated } = useSelector(
    (state: RootState) => ({
      user: state.auth.user,
      theme: state.practice?.theme,
      practiceId: state.practice.id,
      tempColours: state.themeColours,
      isAuthenticated: state.auth.authenticated,
    }),
  );

  const {
    data: loggedInUser,
    isError: isErrorMe,
    isLoading: isLoadingMe,
  } = useGetMeQuery();
  const { data: practices, isFetching: isFetchingPractices } =
    useGetPracticesQuery(
      {
        filtering: { search_term: undefined, status: undefined },
      },
      {
        skip: !user?.show_practice_switcher,
      },
    );

  useGetHeartbeatQuery(undefined, {
    pollingInterval: 60000, // 1m
    skip: !isAuthenticated,
  });

  const [submitLogout] = useLogoutMutation();

  const [switchPractice] = useSwitchPracticeMutation();
  const [endSwitchPractice] = useEndSwitchPracticeMutation();

  const { control, setValue } = useForm<Inputs>({
    defaultValues: {
      practice: undefined,
    },
  });

  const handlePracticeChange = async (value) => {
    setIsSwitching(true);
    value === "admin"
      ? await endSwitchPractice().unwrap()
      : await switchPractice(value).unwrap();

    window.location.assign("/admin");
  };

  const triggerLogout = async () => {
    await submitLogout();
    dispatch(logout());
  };

  const handlePracticeAdminRoute = (e, path) => {
    if (user?.role === UserRole.MWI_ADMIN) {
      e.preventDefault();
      setSwitchPracticeRedirect(path);
    }
  };

  useEffect(() => {
    setValue("practice", practiceId ? practiceId.toString() : undefined);
  }, [practiceId]);

  useEffect(() => {
    if (isErrorMe) {
      dispatch(clearAuth());
      dispatch(clearPractice());
    } else if (loggedInUser) {
      dispatch(login(loggedInUser.user));
      dispatch(setPractice(loggedInUser.practice));
    }
  }, [dispatch, loggedInUser, isErrorMe]);

  if (window.top !== window.self) {
    return (
      <div className="fixed inset-0 flex items-center justify-center px-2">
        <Status
          graphic={<AlertTriangleIcon />}
          danger
          title="Access Restricted"
          description="MWI Pet cannot be accessed from an iframe or popup"
        />
      </div>
    );
  }

  if (isLoadingMe) {
    return (
      <div className="fixed inset-0 flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  return (
    <>
      {user?.role === UserRole.PRACTICE_ADMIN && (
        <>
          <style>{`
            :root {
              ${theme.darkColor && `--mwi-dark: ${tempColours.darkColor ?? theme.darkColor};`}
              ${theme.onDarkColor && `--mwi-on-dark: ${tempColours.onDarkColor ?? theme.onDarkColor};`}
            }
          `}</style>

          <style>{`
            :root {
              ${theme.primaryColor && `--mwi-primary: ${tempColours.primaryColor ?? theme.primaryColor};`}
              ${theme.onPrimaryColor && `--mwi-on-primary: ${tempColours.onPrimaryColor ?? theme.onPrimaryColor};`}
            }
          `}</style>

          <style>{`
            :root {
              ${theme.lightColor && `--mwi-light: ${tempColours.lightColor ?? theme.lightColor};`}
              ${theme.onLightColor && `--mwi-on-light: ${tempColours.onLightColor ?? theme.onLightColor};`}
            }
          `}</style>

          <style>{`
            :root {
              ${theme.lightestColor && `--mwi-lightest: ${tempColours.lightestColor ?? theme.lightestColor};`}
              ${theme.onLightestColor && `--mwi-on-lightest: ${tempColours.onLightestColor ?? theme.onLightestColor};`}
            }
          `}</style>
        </>
      )}

      <div>
        {isAuthenticated && (
          <Navbar>
            <Navbar.Item title="Home" to="/admin" icon={<HomeIcon />} end />
            <Navbar.Item
              title="Customisations"
              to="/admin/customisations"
              icon={<PaletteIcon />}
              onClick={(e) =>
                handlePracticeAdminRoute(e, "/admin/customisations")
              }
            />
            <Navbar.Item
              title="Settings"
              to="/admin/settings"
              icon={<SettingsIcon />}
              onClick={(e) => handlePracticeAdminRoute(e, "/admin/settings")}
            />

            {user?.role === UserRole.MWI_ADMIN && (
              <Navbar.Item
                title="MWI Admin"
                to="/admin/mwi"
                icon={<BuildingHospitalIcon />}
              />
            )}

            <Navbar.Item
              title="Account"
              to="/admin/account"
              icon={<UserCircleIcon />}
            />
          </Navbar>
        )}

        {user?.show_practice_switcher && (
          <SwitchPracticeModal
            isOpen={switchPracticeRedirect !== undefined}
            onClose={() => setSwitchPracticeRedirect(undefined)}
            redirectLocation={switchPracticeRedirect ?? ""}
          />
        )}

        <div
          className={classNames("flex justify-center min-h-screen", {
            "tablet:pl-[80px] desktop:pl-[230px]": isAuthenticated,
          })}
        >
          <div className="flex flex-col flex-grow w-full px-4 tablet:px-16 mx-auto max-w-[1400px]">
            {isAuthenticated && (
              <nav className="hidden w-full tablet:block">
                <div className="border-b border-pinball">
                  <div className="flex justify-end h-[72px] items-center">
                    {user?.show_practice_switcher && (
                      <>
                        <div className="w-full max-w-xs">
                          <Select
                            name="practice"
                            control={control}
                            placeholder="Select a practice"
                            prefixIcon={<BuildingIcon />}
                            size="small"
                            loading={isFetchingPractices || isSwitching}
                            onChange={handlePracticeChange}
                            buttonClassName="w-full"
                          >
                            <SelectOption value="admin">
                              Return to admin
                            </SelectOption>
                            {practices?.map((p) => (
                              <SelectOption
                                key={p.id}
                                value={p.id.toString()}
                                selected={p.id === practiceId}
                              >
                                {p.display_name}
                              </SelectOption>
                            ))}
                          </Select>
                        </div>

                        <div className="h-7 w-[1px] bg-pinball mx-8"></div>
                      </>
                    )}

                    <Dropdown
                      trigger={
                        <Avatar
                          name={`${user?.first_name ?? ""} ${user?.last_name ?? ""}`}
                          className="w-10 h-10"
                        />
                      }
                    >
                      <Dropdown.Item onClick={triggerLogout}>
                        Logout
                      </Dropdown.Item>
                    </Dropdown>
                  </div>
                </div>
              </nav>
            )}

            <main className="flex justify-center flex-grow w-full">
              <div
                className={classNames("flex flex-col w-full py-14", {
                  "mb-14 tablet:mb-0": isAuthenticated,
                })}
              >
                {children}
              </div>
            </main>
          </div>
        </div>
      </div>

      <CookiePopup />
    </>
  );
};

export default App;
